import React, { useEffect } from 'react'
import { Wrap } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Button as SharedButton } from 'shared/components/Button/Button'
import { useUserProvider } from 'shared/components/Context/UserContext'
import { SimpleDialog } from 'shared/components/Dialog/SimpleDialog'
import { Text } from 'shared/components/Text/Text'
import { TextInput } from 'shared/components/TextInput/TextInput'
import { Handler } from 'shared/helpers/typeHelper'
import { bgGray } from 'shared/theme/colors'
import styled from 'styled-components'

import {
  createDeductionCredit,
  DeductionCreditReason,
  DropdownConfig,
  getPaymentPeriodsFromPolicy,
  getPolicyDetails,
  Repayment,
  RepaymentPolicyStatus,
  updateRepaymentPolicy,
} from '../../../models/repayments'
import { DeductionCreditDropdown } from '../DeductionCreditDropdown'
import { startTimeFormatter } from '../PolicyRefinanceForm'

interface PolicyDetailsDialogProps {
  isOpen: boolean
  onClose: Handler
  policy: Repayment
  dialogStatus: RepaymentPolicyStatus | 'DEDUCTION_CREDIT'
  refetchPolicy: () => void
  handlePolicyRedirect: Handler
  isVendor: boolean
}

export function PolicyDetailsDialog({
  isOpen,
  onClose,
  policy,
  dialogStatus,
  refetchPolicy,
  handlePolicyRedirect,
  isVendor,
}: PolicyDetailsDialogProps) {
  const { showBanner } = useUserProvider()
  const status = policy?.repayment_details.policy_status ?? RepaymentPolicyStatus.DRAFT
  const [dialogActionLoading, setDialogActionLoading] = React.useState(false)

  const [terminateReason, setTerminateReason] = React.useState('')
  const [reason, setReason] = React.useState<DeductionCreditReason | undefined>(undefined)

  const paymentPeriods = getPaymentPeriodsFromPolicy(policy)
  const amountOwedPerPeriod =
    policy.repayment_details.amount_owed_per_period === 'N/A'
      ? 'N/A'
      : parseFloat(policy.repayment_details.amount_owed_per_period)

  useEffect(() => {
    if (isOpen) {
      setReason(undefined)
      setTerminateReason('')
    }
  }, [isOpen])

  const confirmReady: boolean = dialogStatus !== 'DEDUCTION_CREDIT' || !!reason

  const onConfirm = async (data: any) => {
    setDialogActionLoading(true)
    if (dialogStatus === 'DEDUCTION_CREDIT') {
      if (reason === undefined) {
        setDialogActionLoading(false)
        return
      }

      const dateScheduled =
        reason === DeductionCreditReason.CORRECTION
          ? startTimeFormatter(data.startDate, data.startTime)
          : undefined

      try {
        await createDeductionCredit(
          reason === DeductionCreditReason.DEBT_WRITE_OFF || reason === DeductionCreditReason.EXTERNAL_PAYMENT
            ? {
                reason,
                amount: data.amount,
                notes: data.notes,
              }
            : {
                reason,
                periodic_amount: data.weeklyDeduction,
                total_billing_amount: data.amount,
                date_scheduled: dateScheduled,
              },
          policy.repayment_details.policy_id
        )
        onClose()
        refetchPolicy()
        handlePolicyRedirect()
      } catch (error: any) {
        if (
          error.response.data?.type === 'client_error' &&
          error.response.data.errors &&
          error.response.data.errors.every((e: any) => !!e.detail)
        )
          showBanner('error', {
            message: error.response.data.errors.map((e: { code: string; detail: string }) => e.detail),
          })
        else if (error.response.data.detail) showBanner('error', { message: error.response.data.detail })
        else showBanner('error', { message: `An error occurred: ${JSON.stringify(error.response.data)}` })
      }

      setDialogActionLoading(false)
    } else {
      try {
        await updateRepaymentPolicy(
          policy.repayment_details.policy_id,
          getPolicyDetails(
            policy.repayment_details.team_id,
            policy.repayment_details,
            status,
            paymentPeriods,
            amountOwedPerPeriod,
            dialogStatus,
            dialogStatus === RepaymentPolicyStatus.TERMINATED ? terminateReason : undefined
          )
        )

        onClose()
        refetchPolicy()
        handlePolicyRedirect()
      } catch (error: any) {
        showBanner('error', {
          message:
            error.response?.data?.detail ?? `An error occurred: ${JSON.stringify(error.response.data)}`,
        })
      }

      setDialogActionLoading(false)
    }
  }

  const renderDeductCredit = () => (
    <Wrap width="100%" styles="flex: 1;">
      <DeductionCreditDropdown
        policy={policy}
        reason={reason}
        setReason={setReason}
        isDisabled={dialogActionLoading}
        onSubmit={onConfirm}
        onCancel={onClose}
        isLoading={dialogActionLoading}
        confirmReady={confirmReady}
        isVendor={isVendor}
      />
    </Wrap>
  )

  const renderDialogContent = (dialogStatus: RepaymentPolicyStatus) => (
    <Wrap>
      <FlexBox isColumn alignItems="flex-start" gap="16px">
        <Text variant="header2" align="left">
          {status === RepaymentPolicyStatus.DRAFT
            ? // special case for draft -> active
              DropdownConfig[dialogStatus].dialogTitle.replace('Resume', 'Activate')
            : DropdownConfig[dialogStatus].dialogTitle}
        </Text>
        <Text variant="paragraph1" align="left">
          {status === RepaymentPolicyStatus.DRAFT
            ? // special case for draft -> active
              DropdownConfig[dialogStatus].dialogDescription.replace('resume', 'start')
            : DropdownConfig[dialogStatus].dialogDescription}
        </Text>
        {DropdownConfig[dialogStatus].cantBeUndone && (
          <Text variant="paragraph1" align="left">
            This action can't be undone.
          </Text>
        )}
        {dialogStatus === RepaymentPolicyStatus.TERMINATED && (
          <FlexBox isFullWidth isColumn alignItems="stretch">
            <CancellationReasonInput
              backgroundColor={bgGray}
              value={terminateReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerminateReason(e.target.value)}
              label="Add cancellation reason"
            />
          </FlexBox>
        )}
      </FlexBox>
    </Wrap>
  )

  return (
    <SimpleDialog isOpen={isOpen} onClose={onClose}>
      <FlexBox isFullHeight isColumn alignItems="center">
        {dialogStatus === 'DEDUCTION_CREDIT' ? renderDeductCredit() : renderDialogContent(dialogStatus)}

        {reason === undefined && (
          <FlexBox isFullWidth isColumn alignItems="center" mt={6}>
            <SharedButton
              variant="primary"
              isLoading={dialogActionLoading}
              isDisabled={!confirmReady}
              onClick={onConfirm}
              mb={3}
            >
              Confirm
            </SharedButton>
            <SharedButton variant="flat" onClick={onClose}>
              Cancel
            </SharedButton>
          </FlexBox>
        )}
      </FlexBox>
    </SimpleDialog>
  )
}

const CancellationReasonInput = styled(TextInput)`
  max-width: none !important;
  width: 100%;
`
